/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useMemo, useState } from "react"
import { Table, TableWrapper, useAsyncEffect } from "@cd2/components"
import { IComponentProp, doo, titleize } from "@cd2/tools/build/es6"
import { TableContext } from "@cd2/components/build/es6/table/useTable"
import { uniq } from "../utils/utils"

function invariant(condition: any, message?: string): asserts condition {
  if (!condition) {
    throw new Error(message ?? "invariant failed")
  }
}

export type ISkeemSort = { by: any; direction: "ASC" | "DESC" | "asc" | "desc" }
type ITableSort<Keys extends string = string> = {
  key: Keys
  direction: "ASC" | "DESC" | "asc" | "desc"
}
// Sorts can come in either case, but a click always emits upper case.
export type ISortClick = { by: any; direction: "ASC" | "DESC" }
type ISortState = [sort: ISkeemSort | null | undefined, onChange: (field: ISortClick) => void]

interface ITableDefinition<NamedColumn extends string = string, SortKeys extends string = string> {
  model: string
  baseAttributes?: any[]
  columns: IColumnDefinition<NamedColumn, SortKeys>[]
}

export type IColumnDefinition<
  NamedColumn extends string = string,
  SortKeys extends string = string,
> = {
  key: NamedColumn
  attributes?: any[]
  sort?: SortKeys | { key: SortKeys; label?: string; by: any | any[] }
  render:
    | ((opts: { sortState?: ISortState }) => React.ReactNode)
    | {
        field: string
        heading?: string
        renderHeading?: () => React.ReactNode
        render?(value: any): React.ReactNode
      }
    | {
        heading: string
        renderHeading?: () => React.ReactNode
        field?: undefined
        render(row: any): React.ReactNode
      }

  // if this is true, then the column will not appear in the columnList property
  // and therefore not in the column visibility controls
  notCustomizable?: boolean
  // if given then this will be the label that appears in the columnList property
  // if not given, then we will humanize the key
  label?: string
  // should this column, be displayed by default
  defaultIncluded?: boolean
}

export type ITableColumnKeys<T extends ITable> = T extends ITable<infer U> ? U : never
export type ITableSortKeys<T extends ITable> = T extends ITable<any, infer U> ? U : never

type CustomColumn = {
  heading?: React.ReactNode
  render(row: any): React.ReactNode
}

type ITableComponentProps<NamedColumn extends string> = {
  className?: string
  data: any[]
  sortState?: ISortState
  columns?: (NamedColumn | CustomColumn)[]
  rowClass?: (row: any) => string
  menuActions?: IComponentProp<(typeof Table)["MenuColumn"], "items">
}

type ITableLoaderComponentProps<NamedColumn extends string, SortKeys extends string> = {
  className?: string
  skeemFilter?: any
  columns?: (NamedColumn | CustomColumn)[]
  skeemSort?: ISkeemSort | ISkeemSort[]
  skeemPaginate?: { size: number; page?: number }
  renderNoResults?: () => React.ReactNode
  defaultSort?: ITableSort<SortKeys>
}

interface IWithColumnRenderersComponentProps<NamedColumn extends string> {
  render(context: {
    renderCell(rowData: unknown, column: NamedColumn | CustomColumn): JSX.Element
    renderHeader(column: NamedColumn | CustomColumn, sortState?: ISortState): JSX.Element
  }): JSX.Element
}

export interface ITable<NamedColumn extends string = string, SortKeys extends string = string> {
  allColumns: NamedColumn[]
  columnList: { key: NamedColumn; name: string }[]
  sortOptions: { key: string; label: string; by: any | any[] }[]

  columnsToSkeemAttributes(columns?: (NamedColumn | CustomColumn)[]): any[]

  renderColumns(columns: (NamedColumn | CustomColumn)[], sortState?: ISortState): JSX.Element

  Table(props: ITableComponentProps<NamedColumn>): JSX.Element

  TableLoader(props: ITableLoaderComponentProps<NamedColumn, SortKeys>): JSX.Element

  WithColumnRenderers(props: IWithColumnRenderersComponentProps<NamedColumn>): JSX.Element

  buildSortOption(
    key: SortKeys,
    direction: "asc" | "desc" | "ASC" | "DESC",
  ): ISkeemSort | ISkeemSort[] | undefined
}

export function createTable<NamedColumn extends string, SortKeys extends string = string>(
  definition: ITableDefinition<NamedColumn, SortKeys>,
): ITable<NamedColumn, SortKeys> {
  function getColumn(name: NamedColumn) {
    const columnDefinition = definition.columns.find(def => def.key === name)

    invariant(columnDefinition, `Could not find column named ${name}`)
    return columnDefinition
  }

  const allColumns = definition.columns.map(def => def.key)

  function isNamedColumn(column: NamedColumn | CustomColumn): column is NamedColumn {
    return typeof column === "string"
  }

  function columnsToSkeemAttributes(columns: (NamedColumn | CustomColumn)[] = allColumns) {
    const attributes: any[] = []

    attributes.push(...(definition.baseAttributes ?? []))

    columns.filter(isNamedColumn).forEach(column => {
      const columnDefinition = getColumn(column)

      const attrs = columnDefinition.attributes ?? []
      attributes.push(...attrs)
    })

    return uniq(attributes)
  }

  function renderColumns(
    columns: Parameters<ITable<NamedColumn>["renderColumns"]>[0],
    sortState?: ISortState,
  ) {
    return (
      <React.Fragment>
        {columns.map(name => (
          <React.Fragment
            key={typeof name === "string" ? name : `custom-${name.heading}-${name.render.name}`}
          >
            {doo(() => {
              if (typeof name === "string") {
                const column = getColumn(name)

                const render = column.render

                if (typeof render === "function") {
                  return render({ sortState })
                }

                const renderRow = (row: any) => {
                  if (render.field) {
                    const value = row[render.field]
                    return render.render ? render.render(value) : value
                  } else {
                    return render.render!(row)
                  }
                }
                const heading =
                  render.renderHeading?.() ??
                  (render.field ? render.heading ?? titleize(render.field) : render.heading)

                if (column.sort) {
                  return (
                    <WithSortableTableHeader
                      sortState={sortState}
                      sortableField={
                        typeof column.sort === "string" ? column.sort : column.sort.key
                      }
                      render={(sortIndicator, onClick) => (
                        <Table.Column
                          children={renderRow}
                          heading={
                            <span onClick={onClick}>
                              {heading} {sortIndicator}
                            </span>
                          }
                        />
                      )}
                    />
                  )
                } else {
                  return <Table.Column children={renderRow} heading={heading} />
                }
              } else {
                return <Table.Column children={name.render} heading={name.heading} />
              }
            })}
          </React.Fragment>
        ))}
      </React.Fragment>
    )
  }

  function TableComp({
    className,
    data,
    sortState,
    columns = allColumns,
    rowClass,
    menuActions,
  }: ITableComponentProps<NamedColumn>) {
    return (
      <div className={className}>
        <Table data={data} keyExtractor={row => row.id} rowClass={rowClass}>
          {renderColumns(columns, sortState)}
          {menuActions && <Table.MenuColumn items={menuActions} />}
        </Table>
      </div>
    )
  }

  function TableLoaderComp({
    className,
    columns = allColumns,
    skeemFilter,
    skeemSort,
    skeemPaginate,
    renderNoResults,
    defaultSort,
  }: ITableLoaderComponentProps<NamedColumn, SortKeys>) {
    const skeem = window.skeem
    const [records, setRecords] = useState<any[] | null>([])
    const memoFilter = useDeepObjectMemo(skeemFilter)
    const memoSkeemSort = useDeepObjectMemo(skeemSort)
    const [sort, setSort] = useState<ITableSort | undefined>(defaultSort)

    useAsyncEffect(async () => {
      const records = await skeem.fetch2<any[]>({
        model: {
          name: definition.model,
          attributes: columnsToSkeemAttributes(
            columns.filter((col): col is NamedColumn => typeof col === "string"),
          ),
          filter: memoFilter,
          sort: skeemSort || (sort ? buildSortOption(sort.key, sort.direction) : undefined),
          paginate: skeemPaginate,
        },
      })
      setRecords(records)
    }, [memoFilter, memoSkeemSort, JSON.stringify(sort)])

    // FIXME: Columns used to implement their own sorting functionality and so
    //        if we pass in a sortState then they will start showing headers
    //        which likely do not line up with the sortOptions. As soon as all
    //        the table columns have been changed to use the integrated `sort`
    //        options, then we can remove this check.
    const canSort =
      definition.columns.filter(
        col => col.sort && !["createdAt", "updatedAt", "lastEditAt"].includes(col.key),
      ).length > 0

    return (
      <div className={className}>
        <TableWrapper status={records === null ? "loading" : "done"}>
          <TableComp
            columns={columns}
            data={records ?? []}
            sortState={
              skeemSort || !canSort
                ? undefined
                : [
                    sort ? { by: sort.key, direction: sort.direction } : undefined,
                    newSort => setSort({ key: newSort.by, direction: newSort.direction }),
                  ]
            }
          />
          {records !== null && records.length === 0 && renderNoResults?.()}
        </TableWrapper>
      </div>
    )
  }

  function WithColumnRenderersComp({ render }: IWithColumnRenderersComponentProps<NamedColumn>) {
    const context = {
      renderCell(rowData: unknown, column: NamedColumn | CustomColumn) {
        if (typeof column === "string") {
          const columnDefinition = getColumn(column)
          return (
            <React.Fragment>
              <TableContext.Provider
                value={{ isHeading: false, row: rowData as any, data: [rowData as any], index: 0 }}
              >
                {doo(() => {
                  if (typeof columnDefinition.render === "function") {
                    return columnDefinition.render({})
                  } else if (columnDefinition.render.field === undefined) {
                    return (
                      <Table.Column
                        children={columnDefinition.render.render}
                        heading={columnDefinition.render.heading}
                      />
                    )
                  } else {
                    return (
                      <Table.DataColumn
                        field={columnDefinition.render.field}
                        heading={
                          columnDefinition.render.heading ?? titleize(columnDefinition.render.field)
                        }
                        render={columnDefinition.render.render}
                      />
                    )
                  }
                })}
              </TableContext.Provider>
            </React.Fragment>
          )
        } else {
          return <React.Fragment>{column.render(rowData)}</React.Fragment>
        }
      },
      renderHeader(column: NamedColumn | CustomColumn, sortState?: ISortState) {
        if (typeof column === "string") {
          const columnDefinition = getColumn(column)
          return (
            <React.Fragment>
              <TableContext.Provider value={{ isHeading: true, data: [] }}>
                {doo(() => {
                  if (typeof columnDefinition.render === "function") {
                    return columnDefinition.render({ sortState })
                  } else if (columnDefinition.render.field === undefined) {
                    return (
                      <Table.Column
                        children={columnDefinition.render.render}
                        heading={columnDefinition.render.heading}
                      />
                    )
                  } else {
                    return (
                      <Table.DataColumn
                        field={columnDefinition.render.field}
                        heading={
                          columnDefinition.render.heading ?? titleize(columnDefinition.render.field)
                        }
                        render={columnDefinition.render.render}
                      />
                    )
                  }
                })}
              </TableContext.Provider>
            </React.Fragment>
          )
        } else {
          return <React.Fragment>{column.render({})}</React.Fragment>
        }
      },
    }

    return render(context)
  }

  const customizableColumns = definition.columns.filter(col => !col.notCustomizable)

  const columnList = customizableColumns.map(col => ({
    key: col.key,
    name: col.label ?? titleize(col.key),
  }))

  const sortOptions = definition.columns
    .filter(col => col.sort)
    .map(col => {
      const sort = col.sort!

      if (typeof sort === "string") {
        return { key: sort, label: titleize(sort), by: sort }
      } else {
        return { key: sort.key, label: sort.label ?? titleize(sort.key), by: sort.by }
      }
    })

  function buildSortOption(key: string, direction: "asc" | "desc" | "ASC" | "DESC") {
    const sortOption = sortOptions.find(option => option.key === key)
    if (sortOption) {
      if (Array.isArray(sortOption.by)) {
        return sortOption.by.map(by => ({ by, direction: direction }))
      } else {
        return { by: sortOption.by, direction: direction }
      }
    } else {
      console.error("Invalid sort option", key)
      return undefined
    }
  }

  return {
    allColumns,
    columnList,
    columnsToSkeemAttributes,
    renderColumns,
    Table: TableComp,
    TableLoader: TableLoaderComp,
    WithColumnRenderers: WithColumnRenderersComp,
    sortOptions,
    buildSortOption,
  }
}

function WithSortableTableHeader({
  sortableField,
  sortState,
  render,
}: {
  sortableField: string
  sortState?: ISortState
  render: (sortIndicator: React.ReactNode, onClick?: () => void) => React.ReactNode
}) {
  function handleSortClick(column: string) {
    if (!sortState) {
      return
    }

    if (!sortState[0]) {
      return sortState[1]({ by: column, direction: "ASC" })
    }

    if (sortState[0].by === column) {
      return sortState[1]({
        by: column,
        direction: sortState[0].direction.toLowerCase() === "asc" ? "DESC" : "ASC",
      })
    } else {
      return sortState[1]({ by: column, direction: "ASC" })
    }
  }

  return sortState && sortableField ? (
    <React.Fragment>
      {render(
        <AnimatedChevron
          dir={
            sortState[0]?.by === sortableField
              ? sortState[0]?.direction.toLowerCase() === "asc"
                ? 1
                : -1
              : 0
          }
          size={16}
        />,
        () => handleSortClick(sortableField),
      )}
    </React.Fragment>
  ) : (
    <React.Fragment>{render(null)}</React.Fragment>
  )
}

export function AnimatedChevron({
  dir,
  onClick,
  size = 32,
}: {
  dir: -1 | 0 | 1
  onClick?: () => void
  size?: number
}) {
  return (
    <div
      onClick={onClick}
      css={css`
        width: ${size}px;
        height: ${size}px;
        display: inline-block;
        vertical-align: middle;
      `}
    >
      <svg
        width={"100%"}
        height={"100%"}
        viewBox="0 0 100 100"
        css={css`
          ${!!onClick && "cursor: pointer;"}
          * {
            transition: 0.3s;
          }

          line {
            stroke: ${dir === 0 ? "#ccc" : "#333"};
            stroke-width: 10;
            stroke-linecap: round;
            transform-origin: 50px 50px;
          }

          ${dir === -1 &&
          css`
            g {
              transform: translate(0, 15%);
            }

            .line1 {
              transform: rotate(40deg);
            }

            .line2 {
              transform: rotate(-40deg);
            }
          `}
          ${dir === 1 &&
          css`
            g {
              transform: translate(0, -15%);
            }

            .line1 {
              transform: rotate(-40deg);
            }

            .line2 {
              transform: rotate(40deg);
            }
          `}
        `}
      >
        <g>
          <line className="line1" x1="10" y1="50" x2="50" y2="50" />
          <line className="line2" x1="90" y1="50" x2="50" y2="50" />
        </g>
      </svg>
    </div>
  )
}

function useDeepObjectMemo<T>(obj: T): T {
  const key = JSON.stringify(obj)
  const memoObj = useMemo<T>(() => obj, [key])
  return memoObj
}
